<template>
  <div class="target-page" v-if="target">
    <router-link
      class="required-course-banner"
      :class="{ 'has-cover': requiredCover }"
      :style="requiredBannerStyle"
      :to="{ name: 'Required' }"
      v-if="lessonData.length > 0"
    >
    </router-link>

    <section class="notice-card" v-if="target.standard">
      <button type="button" class="notice-title" @click="noticeOpen = !noticeOpen">
        <span>企業公告</span>
        <span class="notice-arrow" :class="{ 'is-open': noticeOpen }">⌃</span>
      </button>
      <div class="notice-content" v-show="noticeOpen" v-html="formatText(target.standard)"></div>
    </section>

    <div class="target-section-header">
      <h3>企業標準</h3>
      <button
        v-if="!magEnable"
        type="button"
        class="download-btn"
        :disabled="!target.certificate"
        @click="downloadCertificate"
      >
        證書下載
      </button>
    </div>

    <div class="target-grid">
      <article class="target-card" v-for="item in targetItems" :key="item.key">
        <div class="target-card-header">
          <h4>{{ item.title }}</h4>
          <span v-if="item.done" class="done-badge">已達成</span>
        </div>
        <div class="target-row">
          <span>{{ item.goalLabel }}</span>
          <strong>{{ item.goal }}</strong>
        </div>
        <div class="target-row">
          <span>{{ item.completedLabel }}</span>
          <strong>{{ item.completed }}</strong>
        </div>
        <div class="target-progress">
          <div class="progress-track">
            <div class="progress-fill" :class="{ 'is-warning': !item.done }" :style="{ width: item.percent }"></div>
          </div>
          <span :class="{ 'is-warning': !item.done }">{{ item.percent }}</span>
        </div>
      </article>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import api from '../store/api'

export default {
  name: 'Target',
  data () {
    return {
      noticeOpen: true,
      requiredCover: null,
      requiredInfo: {},
      lessonData: []
    }
  },
  props: {
    target: {
      require: false,
      default: null
    }
  },
  computed: {
    ...mapGetters([
      'authInfo'
    ]),
    magEnable () {
      return Boolean(this.authInfo.user && this.authInfo.user.magEnable)
    },
    requiredBannerStyle () {
      if (!this.requiredCover) {
        return {}
      }
      return {
        backgroundImage: `url(${this.requiredCover})`
      }
    },
    requiredTimeItem () {
      const requiredCourse = this.target.required_course || this.requiredInfo.required_course
      if (!requiredCourse || requiredCourse.rule === '00:00:00') {
        return null
      }

      const requiredSeconds = this.toSeconds(requiredCourse.rule)
      const userSeconds = this.toSeconds(requiredCourse.user)
      const percent = this.toPercent(userSeconds / requiredSeconds)

      return {
        key: 'required_course',
        title: this.translateTitle(requiredCourse.title),
        goalLabel: '目標',
        goal: requiredCourse.rule,
        completedLabel: '已完成',
        completed: requiredCourse.user,
        percent,
        done: requiredCourse.is_pass
      }
    },
    targetItems () {
      if (!this.target) {
        return []
      }

      const itemBuilders = {
        classify: () => {
          if (this.magEnable || !this.target.classify) {
            return null
          }
          return {
            key: 'classify',
            title: this.target.classify.title,
            goalLabel: '目標',
            goal: '完成學前測驗',
            completedLabel: '結果',
            completed: this.target.classify.user ? '完成' : '未完成',
            percent: this.target.classify.user ? '100%' : '0%',
            done: Boolean(this.target.classify.user)
          }
        },
        time: () => {
          if (!this.shouldShow(this.target.time, '00:00:00')) {
            return null
          }
          const percent = this.toPercent(this.convert(this.target.time.user) / this.convert(this.target.time.rule))
          return this.createItem('time', this.target.time, this.target.time.rule, this.target.time.user, percent)
        },
        required_course: () => this.requiredTimeItem,
        required_course_count: () => {
          const requiredCourseCount = this.target.required_course_count
          if (requiredCourseCount.rule <= 0) {
            return null
          }
          const percent = this.toPercent(requiredCourseCount.user / requiredCourseCount.rule)
          return this.createItem(
            'required_course_count',
            requiredCourseCount,
            `${requiredCourseCount.rule} 課`,
            `${requiredCourseCount.user} 課`,
            percent
          )
        },
        course: () => {
          if (!this.shouldShow(this.target.course, 0)) {
            return null
          }
          const percent = this.toPercent(this.target.course.user / this.target.course.rule)
          return this.createItem('course', this.target.course, `${this.target.course.rule} 課`, `${this.target.course.user} 課`, percent)
        },
        exam: () => {
          if (!this.shouldShow(this.target.exam, 0)) {
            return null
          }
          const percent = this.toPercent(this.target.exam.user / this.target.exam.rule)
          return this.createItem('exam', this.target.exam, `${this.target.exam.rule} 次`, `${this.target.exam.user} 次`, percent)
        },
        exam_score: () => {
          if (!this.shouldShow(this.target.exam_score, 0)) {
            return null
          }
          const percent = this.toPercent(this.target.exam_score.user / this.target.exam_score.rule)
          return this.createItem('exam_score', this.target.exam_score, `${this.target.exam_score.rule} 分`, `${this.target.exam_score.user} 分`, percent)
        },
        exam_advance: () => {
          if (!this.shouldShow(this.target.exam_advance, 0)) {
            return null
          }
          const percent = this.toPercent(this.target.exam_advance.user / this.target.exam_advance.rule)
          return this.createItem('exam_advance', this.target.exam_advance, `${this.target.exam_advance.rule} 分`, `${this.target.exam_advance.user > 0 ? this.target.exam_advance.user : 0} 分`, percent)
        },
        exam_advance_with_percent: () => {
          if (!this.shouldShow(this.target.exam_advance_with_percent, 0)) {
            return null
          }
          const percent = this.toPercent(this.target.exam_advance_with_percent.user / this.target.exam_advance_with_percent.rule)
          return this.createItem('exam_advance_with_percent', this.target.exam_advance_with_percent, `${this.target.exam_advance_with_percent.rule} %`, `${this.target.exam_advance_with_percent.user} %`, percent)
        },
        practice: () => {
          if (!this.shouldShow(this.target.practice, 0)) {
            return null
          }
          const percent = this.toPercent(this.target.practice.user / this.target.practice.rule)
          return this.createItem('practice', this.target.practice, `${this.target.practice.rule} 次`, `${this.target.practice.user} 次`, percent)
        }
      }

      return Object.keys(this.target).reduce((items, key) => {
        const buildItem = itemBuilders[key]
        if (!buildItem) {
          return items
        }

        const item = buildItem()
        if (item) {
          items.push(item)
        }
        return items
      }, [])
    }
  },
  mounted () {
    this.getLessonInfo()
  },
  methods: {
    formatText (text) {
      return text.replace(/(\r\n)|(\n)/g, '<br>')
    },
    shouldShow (item, hiddenRule) {
      return item && item.rule !== hiddenRule
    },
    async getLessonInfo () {
      const { data } = await api.get('/contents/courses/required')
      const result = data.data || {}
      const collection = result.data || result.required_courses || result.courses || result.items || result
      this.lessonData = Array.isArray(collection) ? collection : (collection.data || [])
      this.requiredCover = result.cover || result.banner_url || result.banner || data.cover || data.banner_url || data.banner || null
      this.requiredInfo = result
    },
    createItem (key, item, goal, completed, percent) {
      return {
        key,
        title: this.translateTitle(item.title),
        goalLabel: '目標',
        goal,
        completedLabel: '已完成',
        completed,
        percent,
        done: percent === '100%'
      }
    },
    toPercent (num) {
      if (Math.floor(num * 100) >= 100) {
        return '100%'
      } else {
        if (num > 0) {
          return Math.floor(num * 100) + '%'
        } else {
          return '0%'
        }
      }
    },
    toSeconds (time) {
      if (!time && time !== 0) {
        return 0
      }

      if (typeof time === 'number') {
        return time
      }

      if (typeof time === 'string' && time.includes(':')) {
        return this.convert(time)
      }

      const number = Number(time)
      return Number.isNaN(number) ? 0 : number
    },
    translateTitle (title) {
      if (!title) {
        return ''
      }

      if (title.indexOf('.') > -1) {
        return this.$t(title)
      }

      return title
    },
    async downloadCertificate () {
      return api.post('/certificate', {} , {responseType: 'blob'}).then ((response) => {
        if (response) {
          let fileName = 'Certificate'
          const url = URL.createObjectURL(new Blob([response.data],{
            type: 'image/png'
          }))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', fileName)
          document.body.appendChild(link)
          link.click()
        } else {
          return Promise.reject()
        }
      })
    },
    convert (time) {
      return time.split(':').reduce((acc,time) => (60 * acc) + + time)
    }
  }
}
</script>

<style lang="scss" scoped>
.target-page {
  min-height: 50vh;
  padding: 28px 0 56px;
  text-align: left;
}

.required-course-banner {
  position: relative;
  display: flex;
  align-items: center;
  gap: 22px;
  width: 100%;
  min-height: 128px;
  margin: 0 auto 24px;
  padding: 28px 76px 28px 42px;
  color: #fff;
  text-align: left;
  overflow: hidden;
  background: linear-gradient(110deg, #155d92 0%, #1b83a2 55%, #176d86 100%);
  border: 0;
  box-shadow: 0 2px 8px rgba(21, 47, 68, .08);
}

.required-course-banner::after {
  content: "";
  position: absolute;
  right: -90px;
  bottom: -120px;
  width: 420px;
  height: 260px;
  background: rgba(255, 255, 255, .12);
  border-radius: 50%;
  transform: rotate(-12deg);
}

.required-course-banner:hover {
  color: #fff;
  text-decoration: none;
  filter: brightness(1.05);
}

.required-course-banner.has-cover {
  min-height: 128px;
  padding: 0;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
}

.required-course-banner.has-cover::after {
  display: none;
}

.required-course-icon {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  flex: 0 0 56px;
  color: #15618b;
  font-size: 25px;
  background: #fff;
  border-radius: 50%;
}

.required-course-title {
  display: block;
  color: #fff;
  font-size: 26px;
  font-weight: 700;
  line-height: 1.2;
}

.required-course-copy {
  position: relative;
  z-index: 1;
  min-width: 0;
}

.required-course-desc {
  display: block;
  margin-top: 10px;
  color: rgba(255, 255, 255, .92);
  font-size: 16px;
  line-height: 1.45;
}

.required-course-arrow {
  position: absolute;
  z-index: 1;
  top: 50%;
  right: 28px;
  color: rgba(255, 255, 255, .9);
  font-size: 48px;
  line-height: 1;
  transform: translateY(-50%);
}

.notice-card {
  max-width: 1100px;
  margin: 0 auto 30px;
  padding: 30px 34px;
  background: #fff;
  border: 1px solid #c9d9e4;
  border-radius: 5px;
  box-shadow: 0 2px 8px rgba(21, 47, 68, .08);
}

.notice-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px;
  padding: 0;
  color: #0f314a;
  font-size: 17px;
  font-weight: 700;
  background: transparent;
  border: 0;
}

.notice-arrow {
  font-size: 17px;
  transform: rotate(180deg);
  transition: transform .2s ease;
}

.notice-arrow.is-open {
  transform: rotate(0deg);
}

.notice-content {
  color: #111;
  font-size: 16px;
  line-height: 1.65;
}

.target-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1100px;
  margin: 0 auto 16px;
}

.target-section-header h3 {
  margin: 0;
  color: #163c57;
  font-size: 18px;
  font-weight: 700;
}

.download-btn {
  min-width: 84px;
  height: 36px;
  color: #d97450;
  font-size: 15px;
  background: #fff;
  border: 1px solid #e7a181;
  border-radius: 4px;
}

.download-btn:disabled {
  color: #cbb3a8;
  border-color: #efd6c8;
  cursor: not-allowed;
}

.target-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
  max-width: 1100px;
  margin: 0 auto;
}

.target-card {
  min-height: 228px;
  padding: 24px 28px;
  background: #fff;
  border: 1px solid #dde6ed;
  border-radius: 7px;
  box-shadow: 0 2px 8px rgba(21, 47, 68, .08);
}

.target-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 18px;
  margin-bottom: 18px;
  border-bottom: 1px solid #e5edf2;
}

.target-card h4 {
  margin: 0;
  color: #113b5a;
  font-size: 15px;
  font-weight: 700;
  white-space: nowrap;
}

.done-badge {
  padding: 3px 8px;
  color: #26865d;
  font-size: 13px;
  line-height: 1.3;
  background: #e2f4ec;
  border-radius: 2px;
  white-space: nowrap;
}

.target-row {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 16px;
  color: #8ba2b4;
  font-size: 14px;
}

.target-row strong {
  color: #111;
  font-weight: 500;
  text-align: right;
  white-space: nowrap;
}

.target-progress {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 22px;
}

.progress-track {
  flex: 1;
  height: 8px;
  overflow: hidden;
  background: #dce5ed;
  border-radius: 999px;
}

.progress-fill {
  height: 100%;
  background: #22895f !important;
  border-radius: inherit;
}

.progress-fill.is-warning {
  background: #efb51b !important;
}

.target-progress span {
  width: 46px;
  color: #22895f;
  font-size: 12px;
  font-weight: 700;
  text-align: right;
}

.target-progress span.is-warning {
  color: #efb51b;
}

@media (max-width: 1199px) {
  .target-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .target-page {
    padding: 18px 0 36px;
  }

  .notice-card,
  .required-course-banner,
  .target-section-header,
  .target-grid {
    max-width: none;
  }

  .notice-card {
    padding: 22px;
  }

  .target-section-header {
    align-items: flex-start;
    gap: 12px;
  }

  .required-course-banner {
    min-height: 96px;
    gap: 14px;
    padding: 22px 52px 22px 22px;
  }

  .required-course-icon {
    width: 44px;
    height: 44px;
    flex-basis: 44px;
    font-size: 20px;
  }

  .required-course-title {
    font-size: 22px;
  }

  .required-course-desc {
    margin-top: 6px;
    font-size: 14px;
  }

  .required-course-arrow {
    right: 18px;
    font-size: 38px;
  }

  .target-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .target-card {
    min-height: 0;
    padding: 22px;
  }

  .target-card h4 {
    white-space: normal;
  }

  .target-row {
    gap: 12px;
  }

  .target-row strong {
    white-space: normal;
  }
}
</style>
